/**
 * Object-to-Object Assessment read APIs (AiHub Assessment Center).
 *
 * @see assessment.md — /object-to-object/assessments, assessment-pairs, results, relevance, assessments/plan-filter
 */

import { apiClient } from "./apiClient";
import { formatConceptCodeToLabel } from "./autoTaggingService";
import {
  formatOtoObjectLabel,
  normalizeOtoPlan,
  type OtoPlan,
} from "./objectToObjectPlanService";

export const OTO_ASSESSMENTS_DEFAULT_PAGE_SIZE = 20;

export type OtoAssessmentBundleStatus =
  | "init"
  | "running"
  | "success"
  | "failed"
  | "partial";

export type OtoPairStatus =
  | "pending"
  | "tagging"
  | "scoring"
  | "success"
  | "failed_tagging"
  | "failed_scoring"
  | string;

export type OtoRelevanceLevel = "High" | "Medium" | "Low";

export type OtoRelevanceThresholds = {
  high: number;
  medium: number;
};

export type OtoObjectSideSummary = {
  objectType: string;
  objectSubType: string;
  count: number;
};

export type OtoAssessmentListItem = {
  taskId: string;
  uuid: string;
  status: OtoAssessmentBundleStatus | string;
  planId: string;
  totalPairs: number;
  completedCount: number;
  objectA: OtoObjectSideSummary;
  objectB: OtoObjectSideSummary;
  createdAt: string;
  completedAt: string | null;
};

export type OtoMatchedTag = {
  code: string;
  name: string;
  side: "a" | "b" | "both" | string;
};

export type OtoAssessmentPairItem = {
  assessmentId: string;
  uuid: string;
  status: OtoPairStatus;
  objectAType: string;
  objectBType: string;
  objectASubType: string;
  objectBSubType: string;
  objectAId: string;
  objectBId: string;
  objectAVersion: string | null;
  objectBVersion: string | null;
  objectATitle: string | null;
  objectBTitle: string | null;
  objectAVerified: boolean | null;
  objectBVerified: boolean | null;
  relevanceScore: number | null;
  totalWeight: number | null;
  matchedTags: Record<string, OtoMatchedTag[]>;
  createdAt: string;
  completedAt: string | null;
};

export type OtoResultsPair = {
  uuid: string;
  status: OtoPairStatus;
  errorMessage: string | null;
  objectId: string;
  objectType: string;
  objectSubType: string;
  objectVersion: string | null;
  objectTitle: string | null;
  verified: boolean | null;
  relevanceScore: number | null;
  totalWeight: number | null;
  matchedTags: Record<string, OtoMatchedTag[]>;
};

export type OtoResultsGroup = {
  objectId: string;
  objectType: string;
  objectSubType: string;
  objectVersion: string | null;
  objectTitle: string | null;
  verified: boolean | null;
  pairs: OtoResultsPair[];
};

export type OtoAssessmentResults = {
  taskId: string;
  uuid: string;
  status: OtoAssessmentBundleStatus | string;
  groupbyObjectType: string;
  totalPairs: number;
  groups: OtoResultsGroup[];
  objectA: OtoObjectSideSummary;
  objectB: OtoObjectSideSummary;
  objectAType: string;
  objectBType: string;
  createdAt: string | null;
};

export type OtoPaginatedResult<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

export type OtoAssessmentsQuery = {
  page?: number;
  pageSize?: number;
  objectAType?: string;
  objectBType?: string;
  createdFrom?: string;
  createdTo?: string;
};

export type OtoAssessmentPairsQuery = {
  page?: number;
  pageSize?: number;
  objectAType?: string;
  objectBType?: string;
  createdFrom?: string;
  createdTo?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function readStringOrNull(
  record: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (value === null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed || null;
    }
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return null;
}

function readBooleanOrNull(
  record: Record<string, unknown>,
  ...keys: string[]
): boolean | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
    if (value === null) return null;
  }
  return null;
}

function readNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function readNumberOrNull(
  record: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const value = record[key];
    if (value === null) return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

function assertSuccess(result: {
  status?: string;
  message?: string | null;
}): void {
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Request failed.");
  }
}

function extractListPayload(data: unknown): {
  items: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(data)) {
    return { items: data };
  }
  const record = asRecord(data);
  if (!record) return { items: [] };

  const nested = asRecord(record.data);
  const itemsCandidate =
    record.items ??
    record.assessments ??
    record.pairs ??
    record.plans ??
    record.results ??
    nested?.items ??
    nested?.assessments ??
    nested?.pairs ??
    nested?.plans ??
    (Array.isArray(record.data) ? record.data : undefined);

  const items = Array.isArray(itemsCandidate) ? itemsCandidate : [];

  return {
    items,
    total: readNumber(record, "total") ?? (nested ? readNumber(nested, "total") : undefined),
    page: readNumber(record, "page") ?? (nested ? readNumber(nested, "page") : undefined),
    pageSize:
      readNumber(record, "page_size", "pageSize") ??
      (nested ? readNumber(nested, "page_size", "pageSize") : undefined),
  };
}

function displayNameForCode(code: string, explicit?: string): string {
  const named = explicit?.trim();
  if (named) return named;
  const mapped = formatConceptCodeToLabel(code);
  if (mapped && mapped !== code) return mapped;
  const trimmed = code.trim();
  if (!trimmed) return "";
  return trimmed
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function normalizeSideSummary(raw: unknown): OtoObjectSideSummary {
  const record = asRecord(raw);
  if (!record) {
    return { objectType: "", objectSubType: "", count: 0 };
  }
  return {
    objectType: readString(record, "object_type", "type"),
    objectSubType: readString(record, "object_sub_type", "sub_type", "subtype"),
    count: readNumber(record, "count") ?? 0,
  };
}

function normalizeMatchedTags(
  raw: unknown,
): Record<string, OtoMatchedTag[]> {
  const record = asRecord(raw);
  if (!record) return {};
  const out: Record<string, OtoMatchedTag[]> = {};
  for (const [dimension, tagsRaw] of Object.entries(record)) {
    if (!Array.isArray(tagsRaw)) continue;
    const tags: OtoMatchedTag[] = [];
    for (const tagRaw of tagsRaw) {
      const tag = asRecord(tagRaw);
      if (!tag) continue;
      const code = readString(tag, "code");
      if (!code) continue;
      tags.push({
        code,
        name: readString(tag, "name") || displayNameForCode(code),
        side: readString(tag, "side") || "both",
      });
    }
    if (tags.length > 0) out[dimension] = tags;
  }
  return out;
}

export function normalizeOtoAssessmentListItem(
  raw: unknown,
): OtoAssessmentListItem | null {
  const record = asRecord(raw);
  if (!record) return null;
  const taskId = readString(record, "task_id", "assessment_id", "id");
  if (!taskId) return null;
  return {
    taskId,
    uuid: readString(record, "uuid"),
    status: readString(record, "status") || "init",
    planId: readString(record, "plan_id", "planId"),
    totalPairs: readNumber(record, "total_pairs", "totalPairs") ?? 0,
    completedCount: readNumber(record, "completed_count", "completedCount") ?? 0,
    objectA: normalizeSideSummary(record.object_a ?? record.objectA),
    objectB: normalizeSideSummary(record.object_b ?? record.objectB),
    createdAt: readString(record, "created_at", "createdAt"),
    completedAt: readStringOrNull(record, "completed_at", "completedAt"),
  };
}

export function normalizeOtoAssessmentPairItem(
  raw: unknown,
): OtoAssessmentPairItem | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = readString(record, "uuid", "id");
  const assessmentId = readString(record, "assessment_id", "task_id", "assessmentId");
  if (!uuid || !assessmentId) return null;
  return {
    assessmentId,
    uuid,
    status: readString(record, "status") || "pending",
    objectAType: readString(record, "object_a_type", "objectAType"),
    objectBType: readString(record, "object_b_type", "objectBType"),
    objectASubType: readString(
      record,
      "object_a_sub_type",
      "objectASubType",
    ),
    objectBSubType: readString(
      record,
      "object_b_sub_type",
      "objectBSubType",
    ),
    objectAId: readString(record, "object_a_id", "objectAId"),
    objectBId: readString(record, "object_b_id", "objectBId"),
    objectAVersion: readStringOrNull(record, "object_a_version", "objectAVersion"),
    objectBVersion: readStringOrNull(record, "object_b_version", "objectBVersion"),
    objectATitle: readStringOrNull(record, "object_a_title", "objectATitle"),
    objectBTitle: readStringOrNull(record, "object_b_title", "objectBTitle"),
    objectAVerified: readBooleanOrNull(
      record,
      "object_a_verified",
      "objectAVerified",
    ),
    objectBVerified: readBooleanOrNull(
      record,
      "object_b_verified",
      "objectBVerified",
    ),
    relevanceScore: readNumberOrNull(
      record,
      "relevance_score",
      "relevanceScore",
    ),
    totalWeight: readNumberOrNull(record, "total_weight", "totalWeight"),
    matchedTags: normalizeMatchedTags(record.matched_tags ?? record.matchedTags),
    createdAt: readString(record, "created_at", "createdAt"),
    completedAt: readStringOrNull(record, "completed_at", "completedAt"),
  };
}

function normalizeResultsPair(raw: unknown): OtoResultsPair | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = readString(record, "uuid", "id");
  const objectId = readString(record, "object_id", "objectId");
  if (!uuid || !objectId) return null;
  return {
    uuid,
    status: readString(record, "status") || "pending",
    errorMessage: readStringOrNull(record, "error_message", "errorMessage"),
    objectId,
    objectType: readString(record, "object_type", "objectType"),
    objectSubType: readString(record, "object_sub_type", "objectSubType"),
    objectVersion: readStringOrNull(record, "object_version", "objectVersion"),
    objectTitle: readStringOrNull(record, "object_title", "objectTitle"),
    verified: readBooleanOrNull(record, "verified"),
    relevanceScore: readNumberOrNull(
      record,
      "relevance_score",
      "relevanceScore",
    ),
    totalWeight: readNumberOrNull(record, "total_weight", "totalWeight"),
    matchedTags: normalizeMatchedTags(record.matched_tags ?? record.matchedTags),
  };
}

function normalizeResultsGroup(raw: unknown): OtoResultsGroup | null {
  const record = asRecord(raw);
  if (!record) return null;
  const objectId = readString(record, "object_id", "objectId");
  if (!objectId) return null;
  const pairsRaw = record.pairs;
  const pairs = Array.isArray(pairsRaw)
    ? pairsRaw
        .map((item) => normalizeResultsPair(item))
        .filter((item): item is OtoResultsPair => item !== null)
    : [];
  return {
    objectId,
    objectType: readString(record, "object_type", "objectType"),
    objectSubType: readString(record, "object_sub_type", "objectSubType"),
    objectVersion: readStringOrNull(record, "object_version", "objectVersion"),
    objectTitle: readStringOrNull(record, "object_title", "objectTitle"),
    verified: readBooleanOrNull(record, "verified"),
    pairs,
  };
}

export function normalizeOtoAssessmentResults(
  raw: unknown,
): OtoAssessmentResults | null {
  const record = asRecord(raw);
  if (!record) return null;
  const taskId = readString(record, "task_id", "assessment_id", "id");
  if (!taskId) return null;

  const groupsRaw = record.groups;
  const groups = Array.isArray(groupsRaw)
    ? groupsRaw
        .map((item) => normalizeResultsGroup(item))
        .filter((item): item is OtoResultsGroup => item !== null)
    : [];

  const objectA = normalizeSideSummary(record.object_a ?? record.objectA);
  const objectB = normalizeSideSummary(record.object_b ?? record.objectB);
  const groupby = readString(
    record,
    "groupby_object_type",
    "groupbyObjectType",
  );

  return {
    taskId,
    uuid: readString(record, "uuid"),
    status: readString(record, "status") || "init",
    groupbyObjectType: groupby,
    totalPairs: readNumber(record, "total_pairs", "totalPairs") ?? 0,
    groups,
    objectA,
    objectB,
    objectAType:
      objectA.objectType ||
      (groups[0]?.objectType && groupby === groups[0].objectType
        ? groupby
        : "") ||
      groupby,
    objectBType:
      objectB.objectType ||
      groups[0]?.pairs[0]?.objectType ||
      "",
    createdAt: readStringOrNull(record, "created_at", "createdAt"),
  };
}

export function normalizeOtoRelevanceThresholds(
  raw: unknown,
): OtoRelevanceThresholds {
  const record = asRecord(raw);
  const thresholds = asRecord(record?.thresholds) ?? record;
  const high = thresholds ? readNumber(thresholds, "high") : undefined;
  const medium = thresholds ? readNumber(thresholds, "medium") : undefined;
  return {
    high: high ?? 0.67,
    medium: medium ?? 0.34,
  };
}

/** Bidirectional plan label from type names only, e.g. "Insight ⇄ Product". */
export function formatOtoActivePlanLabel(plan: OtoPlan): string {
  const a = plan.objectATypeName.trim() || displayNameForCode(plan.objectAType);
  const b = plan.objectBTypeName.trim() || displayNameForCode(plan.objectBType);
  return `${a || "-"} ⇄ ${b || "-"}`;
}

export function mapBundleStatusToUi(
  status: string,
  completedCount = 0,
): "completed" | "failed" | "partial" | "inProgress" {
  const normalized = status.trim().toLowerCase();
  if (normalized === "success") return "completed";
  if (normalized === "failed") return "failed";
  if (normalized === "partial") return "partial";
  if (completedCount === 0) return "inProgress";
  return "partial";
}

export function formatAssessmentStatusLabel(
  status: string,
  completedCount: number,
  totalPairs: number,
): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === "success") return "Completed";
  if (normalized === "failed") return "Failed";
  if (normalized === "partial") return "Partially";
  if (completedCount === 0) {
    return `${completedCount}/${totalPairs} In Progress`;
  }
  return `${completedCount}/${totalPairs} Completed`;
}

export function formatScoreText(
  relevanceScore: number | null,
  totalWeight: number | null,
): string {
  if (relevanceScore == null || totalWeight == null) return "-";
  return `${relevanceScore}/${totalWeight}`;
}

export function mapRelevanceLevel(
  relevanceScore: number | null,
  totalWeight: number | null,
  thresholds: OtoRelevanceThresholds,
): OtoRelevanceLevel | null {
  if (relevanceScore == null || totalWeight == null || totalWeight <= 0) {
    return null;
  }
  const ratio = relevanceScore / totalWeight;
  if (ratio >= thresholds.high) return "High";
  if (ratio >= thresholds.medium) return "Medium";
  return "Low";
}

export function flattenMatchedTagNames(
  matchedTags: Record<string, OtoMatchedTag[]>,
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const tags of Object.values(matchedTags)) {
    for (const tag of tags) {
      const label = tag.name || tag.code;
      if (!label || seen.has(label)) continue;
      seen.add(label);
      names.push(label);
    }
  }
  return names;
}

export function formatAssessmentDateTime(iso: string | null | undefined): string {
  if (!iso?.trim()) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = String(date.getUTCDate()).padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getUTCMonth()] ?? "";
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

function unitLabelForType(objectType: string, objectSubType: string): string {
  if (objectSubType) {
    const sub = displayNameForCode(objectSubType).toLowerCase();
    if (sub.endsWith("s")) return sub;
    return `${sub}s`;
  }
  const type = displayNameForCode(objectType).toLowerCase();
  if (!type) return "items";
  if (type.endsWith("s")) return type;
  return `${type}s`;
}

export function sideTypeDisplayName(
  objectType: string,
  objectSubType?: string,
): string {
  if (objectSubType) {
    return (
      formatOtoObjectLabel(
        displayNameForCode(objectType),
        displayNameForCode(objectSubType),
      ) || displayNameForCode(objectType)
    );
  }
  return displayNameForCode(objectType) || "-";
}

export function formatObjectSideDetail(
  count: number,
  objectType: string,
  objectSubType: string,
): string {
  return `${count} ${unitLabelForType(objectType, objectSubType)}`;
}

/**
 * Count with the side's own unit: `3 regulations`, `1 device`. Uses
 * `object_sub_type` when present, otherwise `object_type`.
 */
export function formatObjectSideCount(
  count: number,
  objectType: string,
  objectSubType: string,
): string {
  const base = (
    displayNameForCode(objectSubType) || displayNameForCode(objectType)
  ).toLowerCase();
  if (!base) return `${count} ${count === 1 ? "item" : "items"}`;
  if (count === 1 || base.endsWith("s")) return `${count} ${base}`;
  return `${count} ${base}s`;
}

/**
 * Column label for one object side. Insight objects are named after their sub
 * type (e.g. `Regulation`); every other type is named after the type itself.
 */
export function objectSideLabel(
  objectType: string,
  objectSubType: string,
): string {
  if (objectType.trim().toLowerCase() === "insight") {
    return (
      displayNameForCode(objectSubType) || displayNameForCode(objectType) || "-"
    );
  }
  return displayNameForCode(objectType) || "-";
}

/** Plans for Assessment Center filter. */
export async function fetchOtoActivePlans(): Promise<OtoPlan[]> {
  const result = await apiClient.get<unknown>(
    "/object-to-object/assessments/plan-filter",
  );
  assertSuccess(result);
  const extracted = extractListPayload(result.data);
  return extracted.items
    .map((item) => normalizeOtoPlan(item))
    .filter((item): item is OtoPlan => item !== null);
}

export async function fetchOtoAssessments(
  query: OtoAssessmentsQuery = {},
): Promise<OtoPaginatedResult<OtoAssessmentListItem>> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? OTO_ASSESSMENTS_DEFAULT_PAGE_SIZE;
  const params: Record<string, string | number> = {
    page,
    page_size: pageSize,
  };
  if (query.objectAType?.trim()) {
    params.object_a_type = query.objectAType.trim();
  }
  if (query.objectBType?.trim()) {
    params.object_b_type = query.objectBType.trim();
  }
  if (query.createdFrom?.trim()) params.created_from = query.createdFrom.trim();
  if (query.createdTo?.trim()) params.created_to = query.createdTo.trim();

  const result = await apiClient.get<unknown>(
    "/object-to-object/assessments",
    params,
  );
  assertSuccess(result);

  // Pagination fields sit beside `data` on the envelope.
  const envelope = result as {
    data?: unknown;
    total?: number;
    page?: number;
    page_size?: number;
  };
  const extracted = extractListPayload(envelope.data ?? result.data);
  const items = extracted.items
    .map((item) => normalizeOtoAssessmentListItem(item))
    .filter((item): item is OtoAssessmentListItem => item !== null);

  return {
    items,
    total: envelope.total ?? extracted.total ?? items.length,
    page: envelope.page ?? extracted.page ?? page,
    pageSize: envelope.page_size ?? extracted.pageSize ?? pageSize,
  };
}

export async function fetchOtoAssessmentPairs(
  query: OtoAssessmentPairsQuery = {},
): Promise<OtoPaginatedResult<OtoAssessmentPairItem>> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? OTO_ASSESSMENTS_DEFAULT_PAGE_SIZE;
  const params: Record<string, string | number> = {
    page,
    page_size: pageSize,
  };
  if (query.objectAType?.trim()) {
    params.object_a_type = query.objectAType.trim();
  }
  if (query.objectBType?.trim()) {
    params.object_b_type = query.objectBType.trim();
  }
  if (query.createdFrom?.trim()) params.created_from = query.createdFrom.trim();
  if (query.createdTo?.trim()) params.created_to = query.createdTo.trim();

  const result = await apiClient.get<unknown>(
    "/object-to-object/assessment-pairs",
    params,
  );
  assertSuccess(result);

  const envelope = result as {
    data?: unknown;
    total?: number;
    page?: number;
    page_size?: number;
  };
  const extracted = extractListPayload(envelope.data ?? result.data);
  const items = extracted.items
    .map((item) => normalizeOtoAssessmentPairItem(item))
    .filter((item): item is OtoAssessmentPairItem => item !== null);

  return {
    items,
    total: envelope.total ?? extracted.total ?? items.length,
    page: envelope.page ?? extracted.page ?? page,
    pageSize: envelope.page_size ?? extracted.pageSize ?? pageSize,
  };
}

export async function fetchOtoAssessmentById(
  assessmentId: string,
): Promise<OtoAssessmentListItem | null> {
  const id = assessmentId.trim();
  if (!id) return null;
  const result = await apiClient.get<unknown>(
    `/object-to-object/assessments/${encodeURIComponent(id)}`,
  );
  assertSuccess(result);
  return normalizeOtoAssessmentListItem(result.data);
}

export async function fetchOtoAssessmentResults(
  assessmentId: string,
  groupbyObjectType: string,
): Promise<OtoAssessmentResults> {
  const id = assessmentId.trim();
  const groupby = groupbyObjectType.trim();
  if (!id) throw new Error("Assessment id is required.");
  if (!groupby) throw new Error("groupby_object_type is required.");

  const result = await apiClient.get<unknown>(
    `/object-to-object/assessments/${encodeURIComponent(id)}/results`,
    { groupby_object_type: groupby },
  );
  assertSuccess(result);
  const normalized = normalizeOtoAssessmentResults(result.data);
  if (!normalized) {
    throw new Error(result.message?.trim() || "Failed to load assessment results.");
  }
  return normalized;
}

export async function fetchOtoRelevanceThresholds(): Promise<OtoRelevanceThresholds> {
  const result = await apiClient.get<unknown>("/object-to-object/relevance");
  assertSuccess(result);
  return normalizeOtoRelevanceThresholds(result.data);
}
